import React, { useCallback, useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip
} from 'recharts';

// Reusable components
import ReportWidget from './components/ReportWidget';
import CustomTooltip from './components/CustomTooltip';
import { DataPoint } from './components/DataPoint';
import { useTranslation } from 'react-i18next';

// Priority colors matching the plan
const PRIORITY_COLORS = {
  none: '#3b82f6',    // Blue
  low: '#10b981',     // Green
  medium: '#f59e0b',  // Amber
  high: '#ef4444'     // Red
};


/**
 * Summary row displaying priority counts
 */
const SummaryRow = ({
  noneCount,
  lowCount,
  mediumCount,
  highCount,
  onPriorityClick
}) => {
  const { t } = useTranslation();
  return (
  <div className="d-flex flex-wrap justify-content-center justify-content-md-end align-items-center gap-3 gap-md-5 mb-3 py-2">
    <DataPoint
      value={noneCount}
      label={t('shared.none', 'None')}
      color={PRIORITY_COLORS.none}
      onClick={() => onPriorityClick?.('NONE')}
    />

    <div className="border-end d-none d-md-block" style={{ height: '60px' }} />

    <DataPoint
      value={lowCount}
      label={t('shared.low', 'Low')}
      color={PRIORITY_COLORS.low}
      onClick={() => onPriorityClick?.('LOW')}
    />

    <div className="border-end d-none d-md-block" style={{ height: '60px' }} />

    <DataPoint
      value={mediumCount}
      label={t('shared.medium', 'Medium')}
      color={PRIORITY_COLORS.medium}
      onClick={() => onPriorityClick?.('MEDIUM')}
    />

    <div className="border-end d-none d-md-block" style={{ height: '60px' }} />

    <DataPoint
      value={highCount}
      label={t('shared.high', 'High')}
      color={PRIORITY_COLORS.high}
      onClick={() => onPriorityClick?.('HIGH')}
    />
  </div>
);
}

/**
 * Custom label renderer for pie chart segments
 */
const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value }) => {
  if (value === 0) return null;

  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      style={{ fontWeight: 'bold', fontSize: '14px' }}
    >
      {value}
    </text>
  );
};

/**
 * PriorityReportWidget - Displays work order distribution by priority/urgency
 *
 * Shows:
 * - Summary counts for each priority level (None, Low, Medium, High)
 * - Doughnut chart showing distribution
 */
const PriorityReportWidget = ({
  data,
  loading,
  error,
  onPriorityClick,
  onDetailsClick
}) => {
  const { t } = useTranslation();
  const { summary } = data || {};
  const {
    noneCount = 0,
    lowCount = 0,
    mediumCount = 0,
    highCount = 0,
    total = 0
  } = summary || {};
  const isEmpty = total === 0;

  // Prepare chart data
  const chartData = useMemo(() => {
    return [
      { name: t('shared.none', 'None'), value: noneCount, key: 'none', priority: 'NONE' },
      { name: t('shared.low', 'Low'), value: lowCount, key: 'low', priority: 'LOW' },
      { name: t('shared.medium', 'Medium'), value: mediumCount, key: 'medium', priority: 'MEDIUM' },
      { name: t('shared.high', 'High'), value: highCount, key: 'high', priority: 'HIGH' }
    ].filter(item => item.value > 0);
  }, [noneCount, lowCount, mediumCount, highCount, t]);

  // Handle segment click
  const handleSegmentClick = useCallback((data) => {
    if (onPriorityClick && data?.priority) {
      onPriorityClick(data.priority);
    }
  }, [onPriorityClick]);

  return (
    <ReportWidget
      title={t('shared.priority', 'Priority')}
      loading={loading}
      onDetailsClick={onDetailsClick}
      error={error}
      isEmpty={isEmpty}
      emptyIcon={AlertTriangle}
      emptyMessage={t('shared.no_data_available', 'No data available for the selected period.')}
    >
      {/* Summary Data Points */}
      <SummaryRow
        noneCount={noneCount}
        lowCount={lowCount}
        mediumCount={mediumCount}
        highCount={highCount}
        onPriorityClick={onPriorityClick}
      />

      {/* Doughnut Chart */}
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={120}
            label={renderCustomLabel}
            labelLine={false}
            onClick={handleSegmentClick}
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={PRIORITY_COLORS[entry.key]}
                cursor="pointer"
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </ReportWidget>
  );
};

export default PriorityReportWidget;
